/**
 * Resolves Convex connection settings from Vite env vars.
 *
 * Kept as a pure function of an injected env object (rather than reading
 * `import.meta.env` directly) so it's testable without a Vite runtime.
 */

export type ConvexEnv = {
  convexUrl: string;
  /** The `.convex.site` origin better-auth's HTTP routes are mounted on. */
  convexSiteUrl: string;
};

export type ConvexEnvResult = { ok: true; env: ConvexEnv } | { ok: false; error: string };

function deriveSiteUrl(convexUrl: string): string | null {
  try {
    const url = new URL(convexUrl);
    if (!url.hostname.endsWith(".convex.cloud")) {
      return null;
    }
    url.hostname = url.hostname.replace(/\.convex\.cloud$/, ".convex.site");
    return url.origin;
  } catch {
    return null;
  }
}

export function resolveConvexEnv(
  source: Partial<Record<"VITE_CONVEX_URL" | "VITE_CONVEX_SITE_URL", string | undefined>>,
): ConvexEnvResult {
  const convexUrl = source.VITE_CONVEX_URL?.trim();
  if (!convexUrl) {
    return {
      ok: false,
      error:
        "VITE_CONVEX_URL is not set. Copy apps/peek-web/.env.example to .env.local (or set it " +
        "in your deploy environment) with your Convex deployment's URL.",
    };
  }

  const convexSiteUrl = source.VITE_CONVEX_SITE_URL?.trim() || deriveSiteUrl(convexUrl);
  if (!convexSiteUrl) {
    return {
      ok: false,
      error:
        "Could not determine VITE_CONVEX_SITE_URL. Set it explicitly, or use a VITE_CONVEX_URL " +
        "ending in .convex.cloud so it can be derived automatically.",
    };
  }

  return { ok: true, env: { convexUrl, convexSiteUrl } };
}
